<template>
	<v-form ref="form" v-model="valid" @submit.prevent="submit" class="profile-registration-form" :class="[!readonly && 'profile-registration-form--editable']">
		<!-- Mobilenumber -->
		<p class="mb-12" :style="{ 'font-size': large ? '1.25rem' : '1rem' }">
			Profil for
			<strong browserAutocomplete="username" autocomplete="username" class="text-no-wrap">+47 {{ user.mobileNumber || user.mobile | mobile }}</strong>
		</p>
		<v-text-field class="d-none" v-model="user.mobileNumber" label="Mobil*" readonly browserAutocomplete="username" autocomplete="username" />

		<v-row>
			<v-col cols="6" xs="12">
				<v-row>
					<v-col cols="12">
						<v-text-field
							v-model="form.firstName"
							label="Fornavn*"
							class="prime mr-2"
							:class="[large && 'prime--large']"
							placeholder=" "
							:rules="rules.required"
							:disabled="disabled"
							:readonly="readonly"
							required
							autofocus
							browserAutocomplete="given-name"
							autocomplete="given-name"
						/>
					</v-col>
					<v-col cols="12">
						<v-text-field
							v-model="form.lastName"
							label="Etternavn*"
							class="prime"
							:class="[large && 'prime--large']"
							placeholder=" "
							:rules="rules.required"
							:disabled="disabled"
							:readonly="readonly"
							required
							browserAutocomplete="family-name"
							autocomplete="family-name"
						/>
					</v-col>
				</v-row>
				<!-- Sex -->
				<v-radio-group v-model="form.sex" label="Kjønn*" class="prime" :class="[large && 'prime--large']" :column="false" :rules="rules.sex" :disabled="disabled" :readonly="readonly">
					<v-radio label="Kvinne" :value="0" color="primary" :ripple="!readonly" />
					<v-radio label="Mann" :value="1" color="primary" :ripple="!readonly" />
				</v-radio-group>
				<!-- Email -->
				<v-text-field
					v-model="form.email"
					label="E-post*"
					class="prime"
					:class="[large && 'prime--large']"
					placeholder=" "
					type="email"
					:rules="rules.email"
					:disabled="disabled"
					:readonly="readonly"
					required
					browserAutocomplete="email"
					autocomplete="email"
				/>
			</v-col>
			<v-col cols="6" xs="12">
				<v-menu ref="birthdate" v-model="birthDatePicker" transition="scroll-y-transition" :close-on-content-click="false">
					<template v-slot:activator="{ on }">
						<v-text-field
							:value="birthDateFormatted"
							label="Fødselsdato"
							class="prime birth-date--activator mt-2"
							:class="[large && 'prime--large']"
							placeholder=" "
							:disabled="disabled"
							v-on="!readonly ? on : null"
							readonly
							style="margin-top: -1rem; max-width: 275px"
						/>
					</template>

					<v-date-picker ref="birthdate-picker" v-model="birthDatePickerModel" :max="new Date().toISOString().substr(0, 10)" min="1900-01-01" :first-day-of-week="1" locale="nb-NO" :show-current="false" no-title @change="saveBirthDate" />
				</v-menu>
				<!-- Address -->
				<v-text-field v-model="form.address" label="Adresse" class="prime" :class="[large && 'prime--large']" placeholder=" " :disabled="disabled" :readonly="readonly" browserAutocomplete="street-address" autocomplete="street-address" />
				<!-- Postal area/code -->
				<v-row>
					<v-col xs="4">
						<v-text-field
							v-model="form.postalCode"
							label="Post nr/sted"
							class="prime mr-2"
							:class="[large && 'prime--large']"
							placeholder=" "
							v-mask="'####'"
							:disabled="disabled"
							:readonly="readonly"
							browserAutocomplete="postal-code"
							autocomplete="postal-code"
						/>
					</v-col>
					<v-col xs8>
						<v-text-field v-model="form.postalArea" class="prime" browserAutocomplete="address-line1" autocomplete="address-line1" :class="[large && 'prime--large']" :disabled="disabled" :readonly="readonly" />
					</v-col>
				</v-row>

				<v-row>
					<v-col xs="6">
						<v-text-field
							v-model="form.phoneNoHome"
							label="Nummer hjem"
							class="prime mr-2"
							:class="[large && 'prime--large']"
							placeholder=" "
							v-mask="'### ## ###'"
							:disabled="disabled"
							:readonly="readonly"
							browserAutocomplete="mobile"
							autocomplete="mobile"
						/>
					</v-col>
					<v-col xs="6">
						<v-text-field
							v-model="form.phoneNoWork"
							label="Nummer arbeid"
							class="prime mr-2"
							:class="[large && 'prime--large']"
							placeholder=" "
							v-mask="'### ## ###'"
							:disabled="disabled"
							:readonly="readonly"
							browserAutocomplete="mobile"
							autocomplete="mobile"
						/>
					</v-col>
				</v-row>
			</v-col>
		</v-row>

		<!-- Submit -->
		<v-btn v-if="!readonly" depressed :large="large" color="primary" type="submit">{{ submitText }}</v-btn>
		<v-btn v-if="cancellable && !readonly" depressed :large="large" @click="cancel" class="ml-3">Avbryt</v-btn>
	</v-form>
</template>

<script>
import { User } from '@/services/models';
import moment from '@/plugins/moment';

export default {
	name: 'profile-registration-form',
	props: {
		overrideForm: Object,
		cancellable: Boolean,
		disabled: Boolean,
		large: Boolean,
		passwordRequired: {
			type: Boolean,
			default: true,
		},
		readonly: Boolean,
		submitText: {
			type: String,
			default: 'Opprett profil',
		},
		user: {
			type: [User, Object],
			default: () => new User(),
		},
	},
	data() {
		return {
			birthDateFormatted: '',
			birthDatePicker: false,
			form: {
				firstName: '',
				lastName: '',
				address: '',
				postalCode: '',
				postalArea: '',
				birthDate: '',
				phoneNoHome: '',
				phoneNoWork: ''
			},
			passwordVisible: false,
			rules: User.rules,
			valid: false,
		};
	},
	computed: {
		birthDatePickerModel: {
			get() {
				if (!this.form.birthDate) return null;
				return this.$moment(this.form.birthDate).format('YYYY-MM-DD');
			},
			set(value) {
				this.form.birthDate = this.$moment(value);
				if (this.form.birthDate) this.birthDateFormatted = this.$moment(this.form.birthDate).format('L');
			},
		},

		isPasswordRequired() {
			return !!(this.passwordRequired || Object(Object(this.form).password).length);
		},
	},
	methods: {
		cancel() {
			this.form = { ...this.user };
			this.form.mobile = this.user.mobileNumber
			this.$emit('cancel');
		},

		saveBirthDate(date) {
			this.$refs['birthdate'].save(date);
		},

		submit() {
			if (this.valid) {
				this.$emit('submit', new User(this.form));
			} else {
				this.$refs.form.validate();
			}
		},
	},
	filters: {
		mobile(value) {
			if (value) {
				return value.replace(/ /g, '').replace(/^(\d{3})(\d{2})(\d{3}).*/, '$1 $2 $3');
			}
		},
		moment: function (date) {
			return moment(date).format('L');
		},
	},
	watch: {
		birthDatePicker(value) {
			value && setTimeout(() => (this.$refs['birthdate-picker'].activePicker = 'YEAR'));
		},
		user: {
			handler(value) {
				this.form = { ...value };
				this.form.mobile = value.mobileNumber
			},
			immediate: true,
		},
	},
	mounted() {
		// Prefill register user form
		this.form.mobile =  window.CONFIG.user.mobile;
		if (this.overrideForm && this.overrideForm.overrideForm) {
			this.form.firstName = this.overrideForm.overrideForm.receiverFirstName;
			this.form.lastName = this.overrideForm.overrideForm.receiverLastName;
			this.form.address = this.overrideForm.overrideForm.deliveryAddress;
			this.form.postalCode = this.overrideForm.overrideForm.deliveryPostalCode;
			this.form.postalArea = this.overrideForm.overrideForm.deliveryPostalArea;
		} else {
				this.form.firstName = window.CONFIG.user.firstName;
				this.form.lastName = window.CONFIG.user.lastName;
				this.form.address = window.CONFIG.user.address;
				this.form.postalCode = window.CONFIG.user.postalCode;
				this.form.postalArea = window.CONFIG.user.postalArea;
				this.form.sex = window.CONFIG.user.sex;
				this.form.userId = window.CONFIG.user.userId
				this.birthDatePickerModel = window.CONFIG.user.birthDate;
		}
	},
	beforeDestroy() {
		this.$refs.form.reset();
	},
};
</script>

<style lang="scss">
.profile-registration-form {
	&--editable {
		.birth-date--activator {
			.v-text-field__slot > input {
				background-color: var(--v-secondary-lighten1) !important;
			}

			&.v-text-field > .v-input__control > .v-input__slot::before {
				border-color: var(--v-primary-base) !important;
			}
		}
	}
}
</style>
